import { Injectable, inject } from '@angular/core'
import { Observable } from 'rxjs'
import { CollectionType } from '../../enums/mfa.enum'
import { MFAValidateContactModel, MFAValidatePinModel, SecurityQA, SecurityQAReq, TotpEnrollResponse, TotpRequest } from '../../models/Login'
import { SessionTypeEnum } from '../../models/SessionTypeEnum'
import { AppStateService } from '../../store/state/app/app.state.service'
import { UserStateService } from '../../store/state/user/user.state.service'
import { DataService } from '../data.service'
import { EnvironmentService } from '../environment.service'

@Injectable({
  providedIn: 'root'
})
export class MfaService {

  #dataService = inject(DataService)
  #appStateService = inject(AppStateService)
  #userStateService = inject(UserStateService)
  #environmentService = inject(EnvironmentService)
  #validateContact = this.#environmentService.getUrl('ng/v1/user/validatecontact/')
  #validateContactPin = this.#environmentService.getUrl('ng/v1/user/validatecontactpin/')
  #securityQA = this.#environmentService.getUrl('ng/v1/user/securityquestion/')
  #validate = this.#environmentService.getUrl('ng/v1/user/validate/')
  #validatePin = this.#environmentService.getUrl('ng/v1/user/validatepin/')
  #totpEnroll = this.#environmentService.getUrl('ng/v1/user/totp/enroll/')
  #totpConfirm = this.#environmentService.getUrl('ng/v1/user/totp/confirm/')
  #totpValidate = this.#environmentService.getUrl('ng/v1/user/totp/validate/')

  validateContact(contactType: CollectionType, value: string): Observable<MFAValidateContactModel> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const user = this.#userStateService.getUser()
    const payload: MFAValidateContactModel = {
      UserId: user?.UserId,
      SessionType: SessionTypeEnum.MFA
    }
    if (CollectionType.email === contactType) {
      payload.ContactEmail = value
    } else {
      payload.MobilePhone = value.replaceAll('-', '').replaceAll('(', '').replaceAll(')', '')
    }

    const endpoint = this.#validateContact + sessionId
    return this.#dataService.post<MFAValidateContactModel, MFAValidateContactModel>(endpoint, payload)
  }

  validateContactPin(contactType: CollectionType, value: string, pin: number, sessionType : SessionTypeEnum): Observable<MFAValidateContactModel> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const user = this.#userStateService.getUser()
    const payload: MFAValidatePinModel = {
      UserId: user.UserId,
      Pin: pin,
      SessionType: sessionType
    }

    if (CollectionType.email === contactType) {
      payload.ContactEmail = value
    } else if (CollectionType.phone === contactType) {
      payload.MobilePhone = value.replaceAll('-', '').replaceAll('(', '').replaceAll(')', '')
    }

    const endpoint = this.#validateContactPin + sessionId
    return this.#dataService.post<MFAValidatePinModel, MFAValidateContactModel>(endpoint, payload)

  }

  validateSecurityQA(formData: { question: number; answer: string; }): Observable<unknown> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const user = this.#userStateService.getUser()
    const payload: SecurityQAReq = {
      UserId: user?.UserId,
      QuestionId: formData.question,
      QuestionAnswer: formData.answer,
    } as SecurityQAReq

    const endpoint = this.#securityQA + sessionId
    return this.#dataService.put<SecurityQAReq, unknown>(endpoint, payload)

  }

  getSecurityQAs(): Observable<SecurityQA[]> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const endpoint = this.#securityQA + sessionId
    return this.#dataService.get<SecurityQA[]>(endpoint)

  }

  validate(sessionType: SessionTypeEnum): Observable<MFAValidateContactModel> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const user = this.#userStateService.getUser()
    const payload: MFAValidateContactModel = {
      UserId: user?.UserId,
      SessionType: sessionType
    }

    const endpoint = this.#validate + sessionId
    return this.#dataService.post<MFAValidateContactModel, MFAValidateContactModel>(endpoint, payload)
  }

  validatePin(pin: number, sessionType : SessionTypeEnum): Observable<MFAValidateContactModel> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const user = this.#userStateService.getUser()
    const payload: MFAValidatePinModel = {
      UserId: user?.UserId,
      Pin: pin,
      SessionType: sessionType
    }

    const endpoint = this.#validatePin + sessionId
    return this.#dataService.post<MFAValidatePinModel, MFAValidateContactModel>(endpoint, payload)

  }

  // Starts authenticator (TOTP) enrollment — returns the secret / otpauth URI to render the QR.
  totpEnroll(): Observable<TotpEnrollResponse> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const user = this.#userStateService.getUser()
    const endpoint = this.#totpEnroll + sessionId
    return this.#dataService.post<{ UserId: number }, TotpEnrollResponse>(endpoint, { UserId: user?.UserId })
  }

  // Confirms enrollment by validating the first code from the authenticator app.
  totpConfirm(code: string): Observable<MFAValidateContactModel> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const user = this.#userStateService.getUser()
    const payload: TotpRequest = { UserId: user?.UserId, Code: code }
    const endpoint = this.#totpConfirm + sessionId
    return this.#dataService.post<TotpRequest, MFAValidateContactModel>(endpoint, payload)
  }

  // Validates a code from an already-enrolled authenticator app.
  totpValidate(code: string): Observable<MFAValidateContactModel> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const user = this.#userStateService.getUser()
    const payload: TotpRequest = { UserId: user?.UserId, Code: code }
    const endpoint = this.#totpValidate + sessionId
    return this.#dataService.post<TotpRequest, MFAValidateContactModel>(endpoint, payload)
  }

}
